/**
 * Coluna direita do workspace pericial: abas Critérios + Agente. // ux-neutrality:allow doc-comment
 *
 * Extraído de WorkspaceCasoPage (E33.F2) para manter a página dentro do limite // ux-neutrality:allow doc-comment
 * de linhas após a mesa de LR editável. A aba Critérios passa adiante os // ux-neutrality:allow doc-comment
 * handlers de ajuste manual de LR (E33.F2/ADR-0044). // ux-neutrality:allow doc-comment
 */
import { Spinner } from '@/components/ui/Spinner';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { ChatPanel, CriteriaPanel } from '@/components/Workspace';
import { CustomCriteriaSection } from '@/components/Workspace/CustomCriteriaSection';
import { useCustomCriteria } from '@/components/Workspace/useCustomCriteria';
import { ptBR } from '@/i18n/pt-BR';
import type { ChatMessageData } from '@/lib/case_chat';
import type { CaseAnalysisData } from '@/lib/case_workspace';

export interface WorkspaceRightTabsProps {
  messages: ChatMessageData[];
  sending: boolean;
  chatError: string | null;
  pendingCriterion: string | null;
  analysis: CaseAnalysisData;
  onAskAgent: (criterionId: string) => void;
  onSend: (content: string, criterionId: string | null) => void;
  adjusting: boolean;
  onAdjustCriterion: (criterionKey: string, value: number) => Promise<void>;
  onClearCriterion: (criterionKey: string) => Promise<void>;
}

export function WorkspaceRightTabs({
  messages,
  sending,
  chatError,
  pendingCriterion,
  analysis,
  onAskAgent,
  onSend,
  adjusting,
  onAdjustCriterion,
  onClearCriterion,
}: WorkspaceRightTabsProps): JSX.Element {
  // E38.F3: critérios adicionados pelo perito (por análise) — carregados aqui,
  // exibidos abaixo dos 10 canônicos e enviados ao laudo pelo backend.
  const customCriteria = useCustomCriteria(analysis.case_id, analysis.id);
  return (
    <Tabs defaultValue="criterios" data-testid="workspace-right-tabs">
      <TabsList aria-label={ptBR.workspaceCaso.criteriaPanelTitle}>
        <TabsTrigger value="criterios" data-testid="workspace-tab-criterios">
          {ptBR.workspaceCaso.tabCriterios}
        </TabsTrigger>
        <TabsTrigger value="agente" data-testid="workspace-tab-agente">
          {ptBR.workspaceCaso.tabAgente}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="criterios" data-testid="workspace-tabcontent-criterios">
        {analysis.status === 'completed' ? (
          <>
            <CriteriaPanel
              analysis={analysis}
              onAskAgent={onAskAgent}
              adjustment={analysis.lr_adjustment}
              adjusting={adjusting}
              onAdjustCriterion={onAdjustCriterion}
              onClearCriterion={onClearCriterion}
            />
            <CustomCriteriaSection
              criteria={customCriteria.criteria}
              busy={customCriteria.busy}
              error={customCriteria.error}
              onAdd={customCriteria.add}
              onRemove={customCriteria.remove}
            />
          </>
        ) : analysis.status === 'running' ? (
          /* E24.F2.T03: enquanto a análise roda, o placeholder ganha indicador
             vivo (Spinner + mensagem). O anúncio a11y fica no chip do header
             (role=status lá); aqui o conteúdo é visual. Estados não-running
             (ex.: failed) mantêm só o hint estático abaixo. */
          <div
            className="flex flex-col items-center gap-sc-3 border border-dashed border-border p-6"
            data-testid="workspace-criterios-analysing"
          >
            <span className="inline-flex items-center gap-2 text-body-md text-accent-700">
              <Spinner />
              {ptBR.workspaceCaso.caseInAnalysis}
            </span>
            <p className="text-body-sm text-fg-muted">{ptBR.workspaceCaso.tabCriteriosWaiting}</p>
          </div>
        ) : (
          <p className="border border-dashed border-border p-6 text-body-sm text-fg-muted">
            {ptBR.workspaceCaso.tabCriteriosWaiting}
          </p>
        )}
      </TabsContent>
      <TabsContent value="agente" data-testid="workspace-tabcontent-agente">
        <ChatPanel
          messages={messages}
          sending={sending}
          errorMessage={chatError}
          pendingCriterionId={pendingCriterion}
          onSend={onSend}
        />
      </TabsContent>
    </Tabs>
  );
}
